import { useEffect, useMemo, useState } from "react";
import { generateJSON } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import {
  Activity,
  AlertCircle,
  BrainCircuit,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Database,
  ExternalLink,
  Eye,
  FileStack,
  KeyRound,
  RefreshCw,
  Scale,
  ShieldCheck,
  Sparkles,
  Star,
  Plus,
  Pencil,
  UsersRound,
} from "lucide-react";
import {
  clearTemplateCategoryDefault,
  getAiMemories,
  getManagedTemplates,
  getSourceTemplateCatalog,
  getTemplateCallDefaultTemplates,
  getTemplateVersion,
  getTemplateVersions,
  createTemplateDraft,
  runAiOptimization,
  saveTemplateVersionContent,
  saveTemplateVersionSettings,
  saveVersionSignatureConfig,
  setTemplateCategoryDefault,
} from "../lib/api";
import { importEditableSourceTemplate } from "../lib/importEditableSourceTemplate";
import { normalizeTemplateSourceCatalog } from "../lib/templateSourceRecovery";
import { mergeTemplateCallDefaults } from "../lib/templateCallCatalog";
import {
  analyzeAndEditTemplateHtml,
  buildBatchException,
  summarizeBatchEditResult,
} from "../lib/batchTemplateSmartEdit";
import {
  classNames,
  compactText,
  formatBytes,
  formatDate,
} from "../lib/format";
import { auditTemplateApplicability } from "../lib/regionFilter";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  MetricStrip,
  PageHeader,
  ServiceBadge,
} from "./Shared";
import { PORTAL_PORTS, portalUrl } from "../lib/portal";
import { selectTemplateOpeningVersion } from "../lib/templateVersionSelection";
import { useTemplateModules } from "../hooks/useTemplateData";
import TemplateSignatureWorkbench from "./TemplateSignatureWorkbench";

const managedPortals = [
  {
    role: "client",
    title: "文书智能客户端",
    label: "客户端",
    description: "面向企业用户调用模板、开展风险预防并使用 AI 助手。",
    icon: Building2,
  },
  {
    role: "expert",
    title: "文书智能专家端",
    label: "专家端",
    description: "面向专业人员执行文书审校、规则维护和案例沉淀。",
    icon: Scale,
  },
];

const BATCH_EDITOR_EXTENSIONS = [
  StarterKit,
  Table.configure({ resizable: true }),
  TableRow,
  TableHeader,
  TableCell,
  Image,
  Underline,
  Link,
];

function createBatchDocumentJson(editableHtml) {
  return generateJSON(editableHtml || "<p></p>", BATCH_EDITOR_EXTENSIONS);
}

function PortalEntryGrid({ compact = false }) {
  // 三端入口共用同一份门户配置，避免不同管理页面的链接不一致。
  return (
    <div className={classNames("portal-entry-grid", compact && "compact")}>
      {managedPortals.map((portal) => {
        const Icon = portal.icon;
        return (
          <article className="portal-entry-card" data-role={portal.role} key={portal.role}>
            <header>
              <span className="portal-entry-icon"><Icon size={19} aria-hidden="true" /></span>
              <span>
                <small>独立服务</small>
                <strong>{portal.title}</strong>
              </span>
              <ServiceBadge online label="已配置" />
            </header>
            <p>{portal.description}</p>
            <footer>
              <span>端口 {PORTAL_PORTS[portal.role]}</span>
              <a
                className="secondary-button"
                href={portalUrl(portal.role)}
                target="_blank"
                rel="noreferrer"
              >
                {"打开" + portal.label}
                <ExternalLink size={15} aria-hidden="true" />
              </a>
            </footer>
          </article>
        );
      })}
    </div>
  );
}

function PlatformOverview({ catalog, serviceStatus, company, companyRegion, moduleData, moduleStatus, onContextChange }) {
  const largestModules = useMemo(
    () => [...catalog.modules].sort((first, second) => second.fileCount - first.fileCount).slice(0, 8),
    [catalog],
  );
  const currentAudit = useMemo(
    () => (moduleData ? auditTemplateApplicability(moduleData.files || [], companyRegion) : null),
    [companyRegion, moduleData],
  );
  const regionalCount = currentAudit
    ? (currentAudit.layers.district || 0) + (currentAudit.layers.city || 0) + (currentAudit.layers.province || 0)
    : 0;
  const commonCount = currentAudit
    ? (currentAudit.layers.industry || 0) + (currentAudit.layers.national || 0) + (currentAudit.layers.general || 0)
    : 0;

  useEffect(() => {
    onContextChange({ page: "平台总览", currentProject: "", templateName: "", templateId: "" });
  }, [onContextChange]);

  return (
    <>
      <PageHeader
        eyebrow="平台端"
        title="文书智能平台"
        description="统一治理模板资产、企业租户、AI 记忆、专家审校与权限边界。"
        meta={
          <>
            <span>当前租户 {company?.name || "等待业务服务"}</span>
            <span>数据同步 {formatDate(catalog.sourceGeneratedAt)}</span>
          </>
        }
      />
      <MetricStrip
        items={[
          { label: "模板资产", value: catalog.fileCount.toLocaleString("zh-CN"), note: "系统文件索引" },
          { label: "业务模块", value: String(catalog.moduleCount), note: "风险与商业" },
          { label: "资产规模", value: formatBytes(catalog.totalSizeBytes), note: "原始文件总量" },
          {
            label: "业务服务",
            value: serviceStatus === "online" ? "在线" : "本地",
            note: serviceStatus === "online" ? "AI 接口可用" : "等待连接",
          },
        ]}
      />
      {currentAudit ? (
        <MetricStrip
          items={[
            { label: "当前模块可显示", value: currentAudit.visible.toLocaleString("zh-CN"), note: moduleData.moduleName },
            { label: "默认隐藏", value: currentAudit.hidden.toLocaleString("zh-CN"), note: "外地或无关行业" },
            { label: "地区适用", value: regionalCount.toLocaleString("zh-CN"), note: companyRegion?.fullName || "未识别地区" },
            { label: "行业/通用", value: commonCount.toLocaleString("zh-CN"), note: companyRegion?.industrySource || "通用规则" },
          ]}
        />
      ) : moduleStatus === "loading" ? (
        <LoadingState label="正在统计适用范围" />
      ) : null}

      <section className="platform-portal-shortcuts" aria-label="独立端口入口">
        <div className="section-title-row">
          <div>
            <h2>业务端口</h2>
            <p>客户端与专家端独立运行，由平台端统一查看和进入。</p>
          </div>
        </div>
        <PortalEntryGrid compact />
      </section>

      <div className="platform-overview-grid">
        <section className="workspace-section module-distribution">
          <div className="section-title-row">
            <div>
              <h2>资产分布</h2>
              <p>模板数量最多的业务模块。</p>
            </div>
          </div>
          <div className="distribution-list">
            {largestModules.map((module) => (
              <div key={module.moduleKey}>
                <span>{module.moduleName}</span>
                <strong>{module.fileCount.toLocaleString("zh-CN")}</strong>
                <i
                  style={{
                    width:
                      Math.max(
                        4,
                        (module.fileCount / Math.max(1, largestModules[0].fileCount)) * 100,
                      ) + "%",
                  }}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="workspace-section service-monitor">
          <div className="section-title-row">
            <div>
              <h2>运行状态</h2>
              <p>独立客户端与原系统服务连接。</p>
            </div>
          </div>
          <div className="service-status-list">
            <div>
              <Database size={18} aria-hidden="true" />
              <span><strong>模板数据</strong><small>本地同步索引</small></span>
              <ServiceBadge online label="正常" />
            </div>
            <div>
              <BrainCircuit size={18} aria-hidden="true" />
              <span><strong>AI 助手</strong><small>/api/ai/chat</small></span>
              <ServiceBadge online={serviceStatus === "online"} label={serviceStatus === "online" ? "在线" : "未连接"} />
            </div>
            <div>
              <ShieldCheck size={18} aria-hidden="true" />
              <span><strong>权限上下文</strong><small>Foundation Headers</small></span>
              <ServiceBadge online={serviceStatus === "online"} label={serviceStatus === "online" ? "已注入" : "待接入"} />
            </div>
          </div>
        </section>
      </div>

      <section className="governance-flow" aria-label="AI 成长闭环">
        <div><span>1</span><strong>业务使用</strong><small>对话与模板调用</small></div>
        <div><span>2</span><strong>反馈记忆</strong><small>偏好与有效样本</small></div>
        <div><span>3</span><strong>专家审校</strong><small>规则与案例确认</small></div>
        <div><span>4</span><strong>受控优化</strong><small>平台审核后生效</small></div>
      </section>
    </>
  );
}

function PlatformPortals({ onContextChange }) {
  useEffect(() => {
    onContextChange({ page: "端口管理", currentProject: "", templateName: "", templateId: "" });
  }, [onContextChange]);

  return (
    <>
      <PageHeader
        eyebrow="平台端"
        title="端口管理"
        description="查看并进入客户端、专家端；两个业务端保持独立服务、独立路由和固定权限。"
      />
      <section className="portal-management-section">
        <div className="section-title-row">
          <div>
            <h2>业务入口</h2>
            <p>入口会在新窗口打开，不改变当前平台端会话。</p>
          </div>
        </div>
        <PortalEntryGrid />
      </section>
      <section className="portal-boundary-grid" aria-label="端口隔离策略">
        <article>
          <ShieldCheck size={19} aria-hidden="true" />
          <strong>角色固定</strong>
          <p>每个服务在启动时确定角色，浏览器不能通过地址或本地数据切换身份。</p>
        </article>
        <article>
          <KeyRound size={19} aria-hidden="true" />
          <strong>权限固定</strong>
          <p>代理按服务端口注入权限，不接受浏览器自行声明的角色权限。</p>
        </article>
        <article>
          <ExternalLink size={19} aria-hidden="true" />
          <strong>入口单向</strong>
          <p>平台端可进入业务端，客户端和专家端不显示其他端入口。</p>
        </article>
      </section>
    </>
  );
}

function PlatformAssets({ catalog, onContextChange }) {
  const [query, setQuery] = useState("");
  const [selectedModuleKey, setSelectedModuleKey] = useState(catalog.modules[0]?.moduleKey || "");
  const [selectedDirectory, setSelectedDirectory] = useState({ type: "all", path: [], label: "全部模板" });
  const [editingFile, setEditingFile] = useState(null);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [managedAssets, setManagedAssets] = useState([]);
  const [sourceCatalog, setSourceCatalog] = useState({ available: true, stale: false, templates: [], modules: [] });
  const [templateCallDefaults, setTemplateCallDefaults] = useState([]);
  const [sourceStatus, setSourceStatus] = useState("loading");
  const [defaultAction, setDefaultAction] = useState({ templateId: "", message: "", error: false });
  const [newTemplateModuleKey, setNewTemplateModuleKey] = useState(selectedModuleKey);
  const [newTemplateSubmoduleKey, setNewTemplateSubmoduleKey] = useState("");
  const [selectedFileKeys, setSelectedFileKeys] = useState([]);
  const [batchState, setBatchState] = useState({ status: "idle", message: "", results: [], summary: null });
  const [batchResultFilter, setBatchResultFilter] = useState("all");
  const { modulesByKey, status: modulesStatus } = useTemplateModules(catalog.modules.map((module) => module.moduleKey));
  const modules = catalog.modules;

  useEffect(() => {
    onContextChange({ page: "平台模板资产", currentProject: "", templateName: "", templateId: "" });
  }, [onContextChange]);

  async function refreshManagedAssets() {
    try {
      const payload = await getManagedTemplates();
      setManagedAssets(payload.templates || []);
    } catch {
      setManagedAssets([]);
    }
  }

  useEffect(() => { refreshManagedAssets(); }, []);

  useEffect(() => {
    const controller = new AbortController();
    setSourceStatus("loading");
    getSourceTemplateCatalog(controller.signal)
      .then((payload) => {
        const normalized = normalizeTemplateSourceCatalog(payload);
        setSourceCatalog(normalized);
        setSourceStatus(normalized.available ? "ready" : "unavailable");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setSourceCatalog((current) => ({
          ...current,
          available: false,
          message: error.message || "数据库模板源暂时不可用",
        }));
        setSourceStatus("unavailable");
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    getTemplateCallDefaultTemplates({ moduleKey: selectedModuleKey }, controller.signal)
      .then(setTemplateCallDefaults)
      .catch((error) => {
        if (error.name === "AbortError") return;
        setTemplateCallDefaults([]);
      });
    return () => controller.abort();
  }, [selectedModuleKey]);

  const newTemplateModule = catalog.modules.find((item) => item.moduleKey === newTemplateModuleKey) || catalog.modules[0];
  useEffect(() => {
    if (!newTemplateModule?.submodules?.some((item) => item.submoduleKey === newTemplateSubmoduleKey)) {
      setNewTemplateSubmoduleKey(newTemplateModule?.submodules?.[0]?.submoduleKey || "");
    }
  }, [newTemplateModule, newTemplateSubmoduleKey]);

  function selectModule(moduleKey) {
    setSelectedModuleKey(moduleKey);
    setSelectedDirectory({ type: "all", path: [], label: "全部模板" });
    setSelectedFileKeys([]);
  }

  function openNewTemplateDialog() {
    const currentModule = catalog.modules.find((item) => item.moduleKey === selectedModuleKey) || catalog.modules[0];
    setNewTemplateModuleKey(currentModule?.moduleKey || "");
    setNewTemplateSubmoduleKey(
      selectedDirectory.type === "submodule"
        ? selectedDirectory.key
        : currentModule?.submodules?.[0]?.submoduleKey || "",
    );
    setShowNewTemplate(true);
  }

  async function toggleCategoryDefault(file) {
    if (!file.managedTemplateId || !file.published || defaultAction.templateId) return;
    setDefaultAction({ templateId: file.managedTemplateId, message: "", error: false });
    try {
      const result = file.isCategoryDefault
        ? await clearTemplateCategoryDefault(file.managedTemplateId)
        : await setTemplateCategoryDefault(file.managedTemplateId);
      await refreshManagedAssets();
      const catalogPayload = await getSourceTemplateCatalog();
      setSourceCatalog(normalizeTemplateSourceCatalog(catalogPayload));
      setDefaultAction({
        templateId: "",
        message: result.defaultTemplate
          ? `已将“${file.displayName}”设为${file.submoduleName}默认模板`
          : `已取消${file.submoduleName}默认模板`,
        error: false,
      });
    } catch (error) {
      setDefaultAction({ templateId: "", message: error.message || "默认模板设置失败", error: true });
    }
  }

  function fileSelectionKey(file) {
    return file.managedTemplateId || file.id;
  }

  function isBatchEditableFile(file) {
    if (file.templateCallDefault) return false;
    return Boolean(file.managedTemplateId || file.databaseSource || file.sourceRecordId || file.filePath);
  }

  function openAsset(file) {
    if (!file.templateCallDefault) {
      setEditingFile(file);
      return;
    }
    const editableHtml = file.publishedVersion?.editableHtml || file.publishedVersion?.templateHtml || "";
    if (!editableHtml) {
      setDefaultAction({ templateId: "", message: "当前业务默认模板没有可编辑正文，请检查模板调用服务。", error: true });
      return;
    }
    const sourceDocumentId = file.sourceDocumentId || `template-call-default:${file.templateKey}`;
    const upload = new File([editableHtml], file.fileName || `${file.displayName}.html`, {
      type: "text/html;charset=utf-8",
    });
    setEditingFile({
      ...file,
      id: sourceDocumentId,
      sourceDocumentId,
      managedTemplateId: "",
      pendingUpload: upload,
    });
  }

  function selectedFileSet() {
    return new Set(selectedFileKeys);
  }

  function toggleFileSelection(file) {
    const key = fileSelectionKey(file);
    setSelectedFileKeys((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );
  }

  function toggleVisibleSelection(files) {
    const visibleKeys = files.map(fileSelectionKey);
    const current = new Set(selectedFileKeys);
    const allSelected = visibleKeys.length > 0 && visibleKeys.every((key) => current.has(key));
    if (allSelected) {
      setSelectedFileKeys((items) => items.filter((key) => !visibleKeys.includes(key)));
      return;
    }
    setSelectedFileKeys((items) => Array.from(new Set([...items, ...visibleKeys])));
  }

  async function loadEditableVersion(file) {
    let templateId = file.managedTemplateId;
    if (!templateId && (file.databaseSource || file.sourceRecordId || file.filePath)) {
      const imported = await importEditableSourceTemplate(file);
      templateId = imported.templateId;
      if (templateId) await refreshManagedAssets();
    }
    if (!templateId) {
      throw Object.assign(new Error("unmanaged"), { reason: "unmanaged" });
    }
    // 批量处理未纳管源文件时，先自动纳管，再读取真实版本正文，避免把导入接口返回当成可编辑内容。
    const versionsPayload = { id: templateId, versions: (await getTemplateVersions(templateId)).versions || [] };
    const selected = selectTemplateOpeningVersion(versionsPayload);
    const current = selected.current;
    if (!current?.id) throw new Error("该模板没有可编辑版本");
    const details = current.signatureConfig === undefined ? await getTemplateVersion(current.id) : current;
    if (!details.editableHtml) {
      throw Object.assign(new Error("fixed-format"), { reason: "fixed-format" });
    }
    return { templateId, details };
  }

  async function loadWritableDraft(templateId, publishedDetails) {
    if (publishedDetails.status !== "published") return publishedDetails;
    try {
      const createdDraft = await createTemplateDraft(templateId);
      return createdDraft?.id ? await getTemplateVersion(createdDraft.id) : publishedDetails;
    } catch (error) {
      if (error.status !== 409) throw error;
      const versionsPayload = { id: templateId, versions: (await getTemplateVersions(templateId)).versions || [] };
      const reusableDraft = [...versionsPayload.versions]
        .filter((item) => item.status === "draft")
        .sort((left, right) => Number(right.versionNo || 0) - Number(left.versionNo || 0))[0];
      if (!reusableDraft?.id) throw error;
      return getTemplateVersion(reusableDraft.id);
    }
  }

  async function processBatchFile(file) {
    try {
      const { templateId, details } = await loadEditableVersion(file);
      const analysis = analyzeAndEditTemplateHtml({
        html: details.editableHtml,
        pageCount: details.pageCount,
        templateName: file.displayName || file.fileName,
      });
      if (analysis.status === "manual-required") {
        return {
          ...analysis,
          file,
          fileName: file.displayName || file.fileName,
          templateId,
          versionId: details.id,
          versionNo: details.versionNo,
        };
      }
      const documentJson = createBatchDocumentJson(analysis.html);
      const targetVersion = await loadWritableDraft(templateId, details);
      const savedContent = await saveTemplateVersionContent(targetVersion.id, {
        documentJson,
        editableHtml: analysis.html,
        pageCount: Math.max(1, Number(details.pageCount) || analysis.metrics.pageCount || 1),
        lockVersion: Number(targetVersion.lockVersion || details.lockVersion || 0),
      });
      if (!analysis.signatureConfig.fields.length) {
        return {
          ...analysis,
          file,
          fileName: file.displayName || file.fileName,
          templateId,
          versionId: savedContent.id || targetVersion.id,
          versionNo: savedContent.versionNo || targetVersion.versionNo,
        };
      }
      await saveTemplateVersionSettings(savedContent.id || targetVersion.id, { requiresElectronicSignature: true });
      const savedSignature = await saveVersionSignatureConfig(savedContent.id || targetVersion.id, {
        ...analysis.signatureConfig,
        sourceSha256: savedContent.renderedPdfSha256 || savedContent.sourceFileSha256 || targetVersion.renderedPdfSha256 || targetVersion.sourceFileSha256 || "",
        pageCount: Math.max(
          Number(savedContent.pageCount) || 1,
          Number(analysis.signatureConfig.pageCount) || 1,
        ),
      });
      return {
        ...analysis,
        file,
        fileName: file.displayName || file.fileName,
        templateId,
        versionId: savedSignature.id || savedContent.id || targetVersion.id,
        versionNo: savedSignature.versionNo || savedContent.versionNo || targetVersion.versionNo,
      };
    } catch (error) {
      const exception = buildBatchException({
        fileName: file.displayName || file.fileName,
        reason: error.reason || (error.message === "unmanaged" ? "unmanaged" : error.message === "fixed-format" ? "fixed-format" : "failed"),
        message: error.message,
      });
      return { ...exception, file };
    }
  }

  function openBatchCompletedResult(result) {
    if (result?.file) {
      setEditingFile({ ...result.file, preferredVersionId: result.versionId });
    }
  }

  async function runBatchSmartEdit() {
    const filesByKey = new Map(moduleFiles.map((file) => [fileSelectionKey(file), file]));
    const selectedFiles = selectedFileKeys.map((key) => filesByKey.get(key)).filter(Boolean);
    if (!selectedFiles.length || batchState.status === "running") return;
    setBatchState({ status: "running", message: `正在处理 0/${selectedFiles.length}`, results: [], summary: null });
    const results = [];
    setBatchResultFilter("all");
    for (let index = 0; index < selectedFiles.length; index += 1) {
      const result = await processBatchFile(selectedFiles[index]);
      results.push(result);
      setBatchState({
        status: "running",
        message: `正在处理 ${index + 1}/${selectedFiles.length}`,
        results: [...results],
        summary: summarizeBatchEditResult(results),
      });
    }
    await refreshManagedAssets();
    const catalogPayload = await getSourceTemplateCatalog();
    const summary = summarizeBatchEditResult(results);
    const autoCompletedResults = results.filter((result) => result.status === "auto-completed" && result.file);
    setSourceCatalog(normalizeTemplateSourceCatalog(catalogPayload));
    setBatchState({
      status: "done",
      message: "批量智能编辑已完成，自动通过项已保存为草稿，异常项已附处理建议。",
      results,
      summary,
    });
    if (results.length === 1 && autoCompletedResults.length === 1) {
      openBatchCompletedResult(autoCompletedResults[0]);
    }
  }

  if (editingFile) {
    return (
      <TemplateSignatureWorkbench
        file={editingFile}
        catalog={catalog}
        onClose={() => setEditingFile(null)}
        onSaved={refreshManagedAssets}
        onClassificationSaved={async () => {
          await refreshManagedAssets();
          setSourceCatalog(normalizeTemplateSourceCatalog(await getSourceTemplateCatalog()));
        }}
      />
    );
  }

  const activeModule = modulesByKey[selectedModuleKey];
  const activeModuleDefinition = catalog.modules.find((module) => module.moduleKey === selectedModuleKey);
  const managedBySourceId = new Map(managedAssets
    .filter((asset) => asset.sourceDocumentId)
    .map((asset) => [asset.sourceDocumentId, asset]));
  const staticFiles = (activeModule?.files || []).map((file) => {
    const managed = managedBySourceId.get(file.id);
    return managed ? {
      ...file,
      managedTemplateId: managed.id,
      moduleKey: managed.moduleKey,
      moduleName: managed.moduleName,
      submoduleKey: managed.submoduleKey,
      submoduleName: managed.submoduleName,
      published: Boolean(managed.currentPublishedVersionId),
      isCategoryDefault: Boolean(managed.isCategoryDefault),
    } : file;
  }).filter((file) => file.moduleKey === selectedModuleKey);
  const staticSourceIds = new Set(staticFiles.map((file) => file.id));
  const databaseFiles = (sourceCatalog.templates || [])
    .filter((template) => template.moduleKey === selectedModuleKey)
    .map((template) => ({
      id: template.sourceDocumentId,
      sourceRecordId: template.sourceRecordId,
      mergedSourceRecordIds: template.mergedSourceRecordIds,
      managedTemplateId: template.managedTemplateId || "",
      fileName: template.templateName + ".html",
      displayName: template.templateName,
      moduleKey: template.moduleKey,
      moduleName: template.moduleName,
      submoduleKey: template.submoduleKey,
      submoduleName: template.submoduleName,
      categoryPath: template.categoryPath || [],
      categoryPathText: template.categoryPathText,
      sourceUpdateAvailable: template.sourceUpdateAvailable,
      published: template.published,
      isCategoryDefault: template.isCategoryDefault,
      databaseSource: true,
      templateSource: "数据库模板",
      extension: "html",
      sizeBytes: 0,
    }));
  const databaseManagedIds = new Set(databaseFiles.map((file) => file.managedTemplateId).filter(Boolean));
  const managedOnlyFiles = managedAssets
    .filter((asset) => asset.moduleKey === selectedModuleKey
      && !staticSourceIds.has(asset.sourceDocumentId)
      && !databaseManagedIds.has(asset.id))
    .map((asset) => ({
      id: asset.sourceDocumentId || asset.id,
      managedTemplateId: asset.id,
      fileName: asset.sourceFileName || asset.templateName,
      displayName: asset.templateName,
      moduleKey: asset.moduleKey,
      moduleName: asset.moduleName,
      submoduleKey: asset.submoduleKey,
      submoduleName: asset.submoduleName,
      extension: String(asset.sourceFileName || "").split(".").pop(),
      published: Boolean(asset.currentPublishedVersionId),
      isCategoryDefault: Boolean(asset.isCategoryDefault),
      sizeBytes: 0,
    }));
  const moduleFiles = mergeTemplateCallDefaults(
    staticFiles.concat(databaseFiles, managedOnlyFiles),
    templateCallDefaults.filter((file) => file.moduleKey === selectedModuleKey),
  );
  function assetDirectoryText(file) {
    return [file.moduleName, file.submoduleName].filter(Boolean).join(" / ") || file.submoduleName || "";
  }

  function assetFileSourceText(file) {
    return file.templateCallDefault ? `${assetDirectoryText(file)} / 模板调用当前默认` : file.databaseSource
      ? assetDirectoryText(file)
      : file.id;
  }

  const activeFiles = moduleFiles
    .filter((file) => {
      if (selectedDirectory.type === "all") return true;
      if (selectedDirectory.type === "submodule") return file.submoduleKey === selectedDirectory.key;
      return true;
    })
    .filter((file) =>
      [file.fileName, file.submoduleName, file.moduleName, file.categoryPathText, file.templateSource].join(" ").toLowerCase().includes(query.trim().toLowerCase()),
    )
    .sort((left, right) =>
      Number(Boolean(right.templateCallDefault)) - Number(Boolean(left.templateCallDefault))
      || Number(Boolean(right.isCategoryDefault)) - Number(Boolean(left.isCategoryDefault)));
  const selectedKeys = selectedFileSet();
  const batchEditableFiles = activeFiles.filter((file) => isBatchEditableFile(file));
  const selectedActiveFiles = batchEditableFiles.filter((file) => selectedKeys.has(fileSelectionKey(file)));
  const allActiveSelected = batchEditableFiles.length > 0 && batchEditableFiles.every((file) => selectedKeys.has(fileSelectionKey(file)));
  const batchSummary = batchState.summary || summarizeBatchEditResult(batchState.results);
  const batchResultGroups = [
    { key: "auto-completed", label: "自动完成", count: batchSummary.autoCompleted },
    { key: "needs-confirmation", label: "需确认", count: batchSummary.needsConfirmation },
    { key: "manual-required", label: "需人工处理", count: batchSummary.manualRequired },
    { key: "failed", label: "处理失败", count: batchSummary.failed },
  ];
  const visibleBatchResultGroups = batchResultGroups
    .map((group) => ({
      ...group,
      results: batchState.results.filter((item) => item.status === group.key),
    }))
    .filter((group) => batchResultFilter === "all" || group.key === batchResultFilter);

  return (
    <>
      <PageHeader
        eyebrow="平台端"
        title="模板资产"
        description="维护系统模板原件、不可变版本和电子签章配置。"
        actions={
          <button type="button" className="primary-button" onClick={openNewTemplateDialog}>
            <Plus size={16} aria-hidden="true" />新增模板
          </button>
        }
      />
      <section className="asset-management-layout">
        <nav className="asset-module-nav" aria-label="模板业务模块">
          {modules.map((module) => {
            const expanded = module.moduleKey === selectedModuleKey;
            const moduleFileCount = expanded && activeModule ? moduleFiles.length : module.fileCount;
            return (
              <div className={classNames("asset-module-group", expanded && "is-expanded")} key={module.moduleKey}>
                <button
                  type="button"
                  className={classNames("asset-module-button", expanded && "is-active")}
                  aria-expanded={expanded}
                  onClick={() => selectModule(module.moduleKey)}
                >
                  {expanded ? <ChevronDown size={15} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />}
                  <span>{module.moduleName}</span><small>{moduleFileCount.toLocaleString("zh-CN")}</small>
                </button>
                {expanded ? (
                  <div className="asset-submodule-nav" aria-label={`${module.moduleName}业务目录`}>
                    <button
                      type="button"
                      className={selectedDirectory.type === "all" ? "is-active" : ""}
                      onClick={() => setSelectedDirectory({ type: "all", path: [], label: "全部模板" })}
                    >
                      <span>全部模板</span><small>{moduleFiles.length.toLocaleString("zh-CN")}</small>
                    </button>
                    {(module.submodules || []).map((submodule) => {
                      const count = activeModule
                        ? moduleFiles.filter((file) => file.submoduleKey === submodule.submoduleKey).length
                        : submodule.fileCount;
                      return (
                        <button
                          type="button"
                          className={selectedDirectory.type === "submodule" && selectedDirectory.key === submodule.submoduleKey ? "is-active" : ""}
                          key={submodule.submoduleKey}
                          onClick={() => setSelectedDirectory({ type: "submodule", key: submodule.submoduleKey, path: [], label: submodule.submoduleName })}
                        >
                          <span>{submodule.submoduleName}</span><small>{count.toLocaleString("zh-CN")}</small>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
        <div className="asset-table-panel">
        {sourceStatus === "unavailable" || sourceCatalog.stale ? (
          <div className="asset-source-status" role="status">
            <AlertCircle size={15} aria-hidden="true" />
            <span>{sourceCatalog.message || "数据库模板源暂时不可用，静态模板和已纳管模板仍可使用"}</span>
          </div>
        ) : null}
        {defaultAction.message ? (
          <div className={classNames("asset-source-status", defaultAction.error && "is-error")} role="status">
            {defaultAction.error ? <AlertCircle size={15} aria-hidden="true" /> : <CheckCircle2 size={15} aria-hidden="true" />}
            <span>{defaultAction.message}</span>
          </div>
        ) : null}
        <div className="table-toolbar">
          <label className="search-input">
            <Database size={17} aria-hidden="true" />
            <span className="sr-only">搜索当前目录模板</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索当前目录模板"
            />
          </label>
          <span className="asset-current-directory">
            <strong>{activeModuleDefinition?.moduleName || "模板资产"}</strong>
            <small>{selectedDirectory.label} · {activeFiles.length} 份</small>
          </span>
        </div>
        {selectedFileKeys.length ? (
          <div className="batch-edit-toolbar" role="region" aria-label="批量智能编辑">
            <span>
              <strong>已选 {selectedActiveFiles.length} 份</strong>
              <small>自动处理变量、页面排版和签章位置；未纳管源库文件会先自动导入草稿，通过项保存为草稿。</small>
            </span>
            <button type="button" className="secondary-button" onClick={() => setSelectedFileKeys([])} disabled={batchState.status === "running"}>
              清空选择
            </button>
            <button type="button" className="primary-button" onClick={runBatchSmartEdit} disabled={!selectedActiveFiles.length || batchState.status === "running"}>
              <Sparkles size={15} aria-hidden="true" />批量智能编辑
            </button>
          </div>
        ) : null}
        {batchState.status !== "idle" ? (
          <section className="batch-edit-results" aria-label="批量智能编辑结果">
            <header>
              <div>
                <h2>批量处理报告</h2>
                <p>{batchState.message}</p>
              </div>
              <div className="batch-result-metrics" aria-label="批量处理统计">
                {batchResultGroups.map((group) => (
                  <button
                    type="button"
                    key={group.key}
                    className={classNames(batchResultFilter === group.key && "is-active")}
                    onClick={() => setBatchResultFilter(group.key)}
                    disabled={!group.count}
                    aria-label={`查看${group.label}结果`}
                  >
                    <strong>{group.count}</strong><small>{group.label}</small>
                  </button>
                ))}
              </div>
            </header>
            <div className="batch-result-filter-row">
              <button
                type="button"
                className={classNames("secondary-button", batchResultFilter === "all" && "is-active")}
                onClick={() => setBatchResultFilter("all")}
              >
                查看全部结果
              </button>
              {batchResultFilter !== "all" ? (
                <span>当前只显示：{batchResultGroups.find((group) => group.key === batchResultFilter)?.label}</span>
              ) : (
                <span>点击上方统计数字可筛选对应处理结果。</span>
              )}
            </div>
            <div className="batch-result-groups">
              {visibleBatchResultGroups.map((group) => group.results.length ? (
                <div className="batch-result-group" key={group.key}>
                  <h3>{group.label}</h3>
                  {group.results.map((result) => (
                    <article className="batch-result-card" key={(result.templateId || result.file?.id || result.fileName) + group.key}>
                      <div>
                        <strong>{result.fileName}</strong>
                        <small>
                          {result.versionNo ? `已生成草稿 V${result.versionNo}` : result.message || "已分析"}
                        </small>
                      </div>
                      {result.insertedVariables?.length ? (
                        <p>变量：{result.insertedVariables.map((item) => item.name).join("、")}</p>
                      ) : null}
                      {result.missingVariableCandidates?.length ? (
                        <div className="batch-variable-candidates" aria-label="待新增变量">
                          <strong>待新增变量</strong>
                          {result.missingVariableCandidates.map((item) => (
                            <span key={item.label}>
                              {item.label}
                              <em>{item.status === "database-missing" ? "需先接入数据库" : "可加入变量库"}</em>
                            </span>
                          ))}
                        </div>
                      ) : null}
                      {result.metrics ? (
                        <p>排版与签章：已优化版式，生成 {result.metrics.signatureFieldCount} 个签章位置。</p>
                      ) : null}
                      {(result.issues || [result]).map((item) => (
                        <div className="batch-result-issue" key={item.id || item.message}>
                          <span>{item.message}</span>
                          <em>处理建议：{item.recommendation}</em>
                        </div>
                      ))}
                      {result.file ? (
                        <div className="batch-result-actions">
                          {result.status === "auto-completed" ? (
                            <button type="button" className="primary-button" onClick={() => openBatchCompletedResult(result)}>
                              <Eye size={14} />查看完成效果
                            </button>
                          ) : null}
                          <button type="button" className="secondary-button" onClick={() => setEditingFile(result.file)}>
                            <Pencil size={14} />打开人工编辑
                          </button>
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : null)}
            </div>
          </section>
        ) : null}
        <div className="data-table asset-file-table" role="table" aria-label="模板资产文件">
          <div className="data-table-row header" role="row">
            <span role="columnheader" className="asset-select-cell">
              <input
                type="checkbox"
                checked={allActiveSelected}
                disabled={!batchEditableFiles.length || batchState.status === "running"}
                onChange={() => toggleVisibleSelection(batchEditableFiles)}
                aria-label="选择当前列表全部模板"
              />
            </span>
            <span role="columnheader">模板文件</span>
            <span role="columnheader">业务目录</span>
            <span role="columnheader">格式</span>
            <span role="columnheader">状态</span>
            <span role="columnheader">操作</span>
          </div>
          {activeFiles.map((file) => (
            <div className="data-table-row" role="row" key={file.id}>
              <span role="cell" className="asset-select-cell">
                <input
                  type="checkbox"
                  checked={selectedKeys.has(fileSelectionKey(file))}
                  disabled={batchState.status === "running" || !isBatchEditableFile(file)}
                  onChange={() => toggleFileSelection(file)}
                  aria-label={`选择${file.displayName || file.fileName}`}
                />
              </span>
              <span role="cell" className="asset-file-cell">
                <FileStack size={17} aria-hidden="true" />
                <span className="asset-file-copy">
                  <strong>{file.fileName}</strong>
                  <small>{assetFileSourceText(file)}</small>
                </span>
              </span>
              <span role="cell">{assetDirectoryText(file) || file.submoduleName}</span>
              <span role="cell">{String(file.extension || "").toUpperCase()}</span>
              <span role="cell" className="asset-status-cell">
                {file.templateCallDefault ? <i className="status-chip default">业务当前默认</i>
                  : file.isCategoryDefault ? <i className="status-chip default">分类默认</i>
                  : file.sourceUpdateAvailable ? <i className="status-chip warning">有源更新</i>
                    : file.published ? <i className="status-chip success">已发布</i>
                    : !file.managedTemplateId ? <i className="status-chip">未纳管</i>
                      : <i className="status-chip warning">草稿</i>}
              </span>
              <span role="cell" className="asset-file-actions">
                <button
                  type="button"
                  className={classNames("icon-button asset-default-button", file.isCategoryDefault && "is-active")}
                  onClick={() => toggleCategoryDefault(file)}
                  disabled={file.templateCallDefault || !file.managedTemplateId || !file.published || Boolean(defaultAction.templateId)}
                  aria-label={file.isCategoryDefault ? "取消分类默认模板" : "设为分类默认模板"}
                  title={file.templateCallDefault
                    ? "业务当前默认由模板调用服务维护"
                    : !file.managedTemplateId || !file.published
                    ? "模板发布后才能设为分类默认"
                    : file.isCategoryDefault ? "取消分类默认模板" : "设为分类默认模板"}
                >
                  <Star size={15} fill={file.isCategoryDefault ? "currentColor" : "none"} />
                </button>
                <button type="button" className="secondary-button asset-edit-button" onClick={() => openAsset(file)}>
                  <Pencil size={14} />{file.templateCallDefault ? "纳管编辑" : "编辑"}
                </button>
              </span>
            </div>
          ))}
          {modulesStatus === "loading" ? <LoadingState label="正在读取模板文件" /> : null}
        </div>
        </div>
      </section>
      {showNewTemplate ? (
        <div className="template-new-dialog" role="dialog" aria-modal="true" aria-label="新增模板">
          <div className="template-new-backdrop" onClick={() => setShowNewTemplate(false)} />
          <form className="template-new-panel" onSubmit={(event) => event.preventDefault()}>
            <header><div><h2>新增系统模板</h2><p>选择业务目录并上传 DOC、DOCX 或 PDF，最大 12 MB。</p></div><button type="button" className="icon-button" onClick={() => setShowNewTemplate(false)}>×</button></header>
            <label><span>模板名称</span><input id="newTemplateName" required /></label>
            <label><span>所属模块</span><select id="newTemplateModule" value={newTemplateModuleKey} onChange={(event) => setNewTemplateModuleKey(event.target.value)}>{catalog.modules.map((module) => <option key={module.moduleKey} value={module.moduleKey}>{module.moduleName}</option>)}</select></label>
            <label><span>业务目录</span><select id="newTemplateSubmodule" value={newTemplateSubmoduleKey} onChange={(event) => setNewTemplateSubmoduleKey(event.target.value)}>{(newTemplateModule?.submodules || []).map((submodule) => <option key={submodule.submoduleKey} value={submodule.submoduleKey}>{submodule.submoduleName}</option>)}</select></label>
            <label><span>原文件</span><input id="newTemplateFile" type="file" accept=".doc,.docx,.pdf" required /></label>
            <label className="electronic-sign-toggle"><input id="newTemplateElectronic" type="checkbox" defaultChecked /><span><strong>用于电子签署</strong><small>创建后进入签章配置工作台</small></span></label>
            <footer><button type="button" className="secondary-button" onClick={() => setShowNewTemplate(false)}>取消</button><button type="button" className="primary-button" onClick={() => {
              const upload = document.getElementById("newTemplateFile")?.files?.[0];
              const name = document.getElementById("newTemplateName")?.value?.trim();
              if (!upload || !name) return;
              const moduleKey = newTemplateModuleKey;
              const module = catalog.modules.find((item) => item.moduleKey === moduleKey);
              const submodule = module?.submodules?.find((item) => item.submoduleKey === newTemplateSubmoduleKey);
              const requiresElectronicSignature = document.getElementById("newTemplateElectronic")?.checked !== false;
              setEditingFile({ id: `new-${Date.now()}`, fileName: upload.name, displayName: name, moduleKey, moduleName: module?.moduleName || "", submoduleKey: submodule?.submoduleKey || "", submoduleName: submodule?.submoduleName || "", requiresElectronicSignature, pendingUpload: upload });
              setShowNewTemplate(false);
            }}>创建草稿</button></footer>
          </form>
        </div>
      ) : null}
    </>
  );
}

function PlatformIntelligence({ company, onContextChange }) {
  const [memories, setMemories] = useState([]);
  const [status, setStatus] = useState("loading");
  const [optimization, setOptimization] = useState(null);

  useEffect(() => {
    onContextChange({ page: "AI 成长引擎", currentProject: "", templateName: "", templateId: "" });
  }, [onContextChange]);

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    getAiMemories(
      {
        userId: "document-hub-local",
        companyId: company?.id,
      },
      controller.signal,
    )
      .then((payload) => {
        setMemories(payload.memories || []);
        setStatus("ready");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setStatus("error");
      });
    return () => controller.abort();
  }, [company?.id]);

  async function optimize() {
    setOptimization({ state: "running", message: "正在汇总反馈与模板样本" });
    try {
      const result = await runAiOptimization();
      setOptimization({
        state: "done",
        message: result.message || "优化任务已完成",
        suggestions: result.suggestions,
      });
    } catch (error) {
      setOptimization({ state: "error", message: error.message });
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="平台端"
        title="AI 成长引擎"
        description="查看长期记忆、反馈样本和优化建议，所有改进均保留人工治理入口。"
        actions={
          <button className="primary-button" type="button" onClick={optimize} disabled={optimization?.state === "running"}>
            <RefreshCw className={optimization?.state === "running" ? "spin" : ""} size={16} aria-hidden="true" />
            运行优化
          </button>
        }
      />

      <div className="intelligence-grid">
        <section className="workspace-section memory-panel">
          <div className="section-title-row">
            <div>
              <h2>长期记忆</h2>
              <p>用户偏好、业务习惯和模板实践。</p>
            </div>
            <span>{memories.length} 条</span>
          </div>
          {status === "loading" ? (
            <LoadingState label="正在读取记忆" />
          ) : status === "error" ? (
            <EmptyState title="记忆服务未连接" description="启动原系统后端后可查看真实记忆。" />
          ) : memories.length ? (
            <div className="memory-list">
              {memories.map((memory) => (
                <article key={memory.id}>
                  <BrainCircuit size={17} aria-hidden="true" />
                  <div>
                    <span>{memory.memory_type || "业务记忆"} / {memory.module_name || "通用"}</span>
                    <p>{compactText(memory.memory_value, 180)}</p>
                    <small>可信度 {memory.confidence || "-"} / 使用 {memory.hit_count || 0} 次</small>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState title="暂无长期记忆" description="完成对话并提交反馈后，偏好会在此沉淀。" />
          )}
        </section>

        <section className="workspace-section optimization-panel">
          <div className="section-title-row">
            <div>
              <h2>本轮优化</h2>
              <p>由平台主动运行，结果不会自动改写法律规则。</p>
            </div>
          </div>
          {optimization ? (
            <div className={classNames("optimization-result", optimization.state)}>
              {optimization.state === "done" ? <CheckCircle2 size={22} /> : optimization.state === "error" ? <AlertCircle size={22} /> : <Activity className="pulse" size={22} />}
              <strong>{optimization.message}</strong>
              {typeof optimization.suggestions === "number" ? <span>生成 {optimization.suggestions} 条优化建议</span> : null}
            </div>
          ) : (
            <div className="optimization-checklist">
              <div><CheckCircle2 size={16} /><span>聚合最近 30 天用户反馈</span></div>
              <div><CheckCircle2 size={16} /><span>归纳模板生成与审校样本</span></div>
              <div><CheckCircle2 size={16} /><span>输出待平台审核的优化建议</span></div>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function PlatformTenants({ company, serviceStatus, onContextChange }) {
  useEffect(() => {
    onContextChange({ page: "租户策略", currentProject: "", templateName: "", templateId: "" });
  }, [onContextChange]);

  return (
    <>
      <PageHeader
        eyebrow="平台端"
        title="租户策略"
        description="企业身份、模板可见范围和 AI 记忆按租户隔离。"
      />
      <section className="tenant-panel">
        <div className="tenant-identity">
          <div><Building2 size={22} aria-hidden="true" /></div>
          <span>
            <small>当前企业</small>
            <strong>{company?.name || "未连接企业上下文"}</strong>
            <em>{company?.id || "等待 Foundation Context"}</em>
          </span>
          <ServiceBadge online={serviceStatus === "online"} label={serviceStatus === "online" ? "上下文有效" : "待连接"} />
        </div>
        <div className="tenant-policy-grid">
          <article><Database size={18} /><strong>系统模板</strong><p>所有授权租户可查看，调用时保留系统来源。</p></article>
          <article><Building2 size={18} /><strong>公司专用</strong><p>仅当前企业可见，可覆盖同场景系统模板。</p></article>
          <article><UsersRound size={18} /><strong>个人默认</strong><p>绑定企业与使用人，仅本人业务上下文可调用。</p></article>
          <article><BrainCircuit size={18} /><strong>AI 记忆</strong><p>按用户、企业和模块分层存储，可审计使用次数。</p></article>
        </div>
      </section>
    </>
  );
}

function PlatformPermissions({ onContextChange }) {
  useEffect(() => {
    onContextChange({ page: "权限审计", currentProject: "", templateName: "", templateId: "" });
  }, [onContextChange]);

  const rows = [
    ["查看系统模板", true, true, true],
    ["调用公司模板", true, true, false],
    ["提交 AI 反馈", true, true, true],
    ["审校与规则沉淀", false, true, true],
    ["运行成长优化", false, false, true],
    ["配置租户与权限", false, false, true],
  ];

  return (
    <>
      <PageHeader
        eyebrow="平台端"
        title="权限审计"
        description="客户端、专家端和平台端按最小权限访问模板与 AI 能力。"
      />
      <section className="permission-matrix" role="table" aria-label="三端权限矩阵">
        <div className="permission-row header" role="row">
          <span role="columnheader">能力</span>
          <span role="columnheader">客户端</span>
          <span role="columnheader">专家端</span>
          <span role="columnheader">平台端</span>
        </div>
        {rows.map((row) => (
          <div className="permission-row" role="row" key={row[0]}>
            <span role="cell"><KeyRound size={16} />{row[0]}</span>
            {row.slice(1).map((allowed, index) => (
              <span role="cell" key={index}>
                {allowed ? <CheckCircle2 className="allowed" size={18} aria-label="允许" /> : <span className="not-allowed">-</span>}
              </span>
            ))}
          </div>
        ))}
      </section>
    </>
  );
}

export default function PlatformWorkspace({
  view,
  catalog,
  catalogStatus,
  catalogError,
  onCatalogRetry,
  company,
  companyRegion,
  moduleData,
  moduleStatus,
  serviceStatus,
  onContextChange,
}) {
  if (catalogStatus === "loading") return <LoadingState label="正在读取平台资产" />;
  if (catalogStatus === "error" || !catalog) {
    return <ErrorState message={catalogError} onRetry={onCatalogRetry} />;
  }

  if (view === "portals") return <PlatformPortals onContextChange={onContextChange} />;
  if (view === "assets") return <PlatformAssets catalog={catalog} onContextChange={onContextChange} />;
  if (view === "intelligence") return <PlatformIntelligence company={company} onContextChange={onContextChange} />;
  if (view === "tenants") {
    return <PlatformTenants company={company} serviceStatus={serviceStatus} onContextChange={onContextChange} />;
  }
  if (view === "permissions") return <PlatformPermissions onContextChange={onContextChange} />;
  return (
    <PlatformOverview
      catalog={catalog}
      serviceStatus={serviceStatus}
      company={company}
      companyRegion={companyRegion}
      moduleData={moduleData}
      moduleStatus={moduleStatus}
      onContextChange={onContextChange}
    />
  );
}
